import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Store } from "@mui/icons-material";

// Components
import SearchComponent from "../../components/Search";
import PaginationComponentOne from "../../components/Pagination/Pagination-O";

// Redux actions
import { getAllStores } from "../../redux/StoreManagement/storeManagementActions";

const StoresTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux state
  const { 
    storesData, 
    storesTotalCount, 
    storesLoading, 
    storesError,
    storesCurrentPage,
  } = useSelector((state) => state.storeManagement);

  // Local state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [activeTab, setActiveTab] = useState("All");

  // Tab options
  const tabs = [
    { id: "All", label: "All", count: activeTab === "All" ? (storesTotalCount || 0) : 0 },
    { id: "Active", label: "Active", count: activeTab === "Active" ? (storesData?.filter(store => store.status === "Active").length || 0) : 0 },
    { id: "Inactive", label: "Inactive", count: activeTab === "Inactive" ? (storesData?.filter(store => store.status === "Inactive").length || 0) : 0 },
  ];

  // For server-side pagination, we use the data directly from Redux
  // Client-side filtering is now handled by the API
  const displayData = useMemo(() => {
    if (!storesData || !Array.isArray(storesData)) return [];
    
    // If using server-side pagination and filtering, return data as-is
    // Tab filtering would also need to be handled server-side
    if (activeTab === "All") return storesData;
    
    // For now, keep client-side tab filtering until API supports it
    return storesData.filter(store => store.status === activeTab);
  }, [storesData, activeTab]);

  const getStatusParam = (tab) => {
  if (tab === "Active") return "active";
  if (tab === "Inactive") return "inactive";
  return undefined;
};


  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    setCurrentPage(1);
    dispatch(getAllStores({
      page: 1,
      page_size: pageSize,
      search: value,
      ...(getStatusParam(activeTab) && { status: getStatusParam(activeTab) })
    }));
  };

  // Handle pagination - using ProductInventoryPage approach
const handlePageChange = (paginationData) => {
  let page = currentPage;
  let size = pageSize;
  if (typeof paginationData === 'object') {
    if (paginationData.page !== undefined) page = paginationData.page;
    if (paginationData.pageSize !== undefined && paginationData.pageSize !== pageSize) {
      size = paginationData.pageSize;
      page = 1;
      setPageSize(size);
    }
  } else {
    page = paginationData;
  }
  setCurrentPage(page);
  dispatch(getAllStores({
    page,
    page_size: size,
    search: searchValue,
    ...(getStatusParam(activeTab) && { status: getStatusParam(activeTab) })
  }));
};

  // Handle tab change
const handleTabChange = (tabId) => {
  setActiveTab(tabId);
  setCurrentPage(1);
  setSearchValue("");
  dispatch(getAllStores({
    page: 1,
    page_size: pageSize,
    search: "",
    ...(getStatusParam(tabId) && { status: getStatusParam(tabId) })
  }));
};

  // Handle actions
  const handleViewStore = (storeId) => {
    navigate(`/store-management/stores/${storeId}`);
  };

  // Initial fetch
  useEffect(() => {
    dispatch(getAllStores({
      page: 1,
      page_size: pageSize,
      search: "",
       ...(getStatusParam(activeTab) && { status: getStatusParam(activeTab) })
    }));
  }, [dispatch, pageSize, activeTab]);

  return (
    <div className="bg-white rounded-lg border border-[#E1E6E1]">
      {/* Header */}
      <div className="p-4 border-b border-[#E1E6E1]">
        <div className="flex flex-col gap-4 mb-4">
          <h3 className="text-lg font-semibold text-[#161D17]">All Stores</h3>
          
          {/* Search and Export on same line */}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <SearchComponent
                searchValue={searchValue}
                handleChange={handleSearch}
                placeholder="Search stores..."
              />
            </div>
            <button className="flex items-center gap-2 border border-[#2B8C34] text-[#2B8C34] px-6 py-2.5 rounded-lg hover:bg-[#2B8C34] hover:text-white transition-colors whitespace-nowrap font-medium min-h-[50px]">
              <svg 
                className="w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                />
              </svg>
              <span className="text-sm font-medium">Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs - Using Inventory Design Pattern */}
      <div className="px-4 pt-4">
        <div className="flex items-center border max-w-2xl border-[#E1E6E1] bg-[#F6F7F6] rounded-lg overflow-hidden">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-3 py-2 text-center flex-1 text-sm font-semibold flex items-center justify-center gap-2 ${
                activeTab === tab.id ? "bg-white rounded-lg" : "bg-[#F6F7F6]"
              }`}
            >
              <span>{tab.label}</span>
              <span className={`px-2 py-1 rounded-[4px] text-xs font-medium ${
                activeTab === tab.id
                  ? 'bg-[#DBFFDF] text-[#2B8C34]'
                  : 'bg-[#E1E6E1] text-[#5C715E]'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Table Content */}
      <div className="overflow-x-auto relative py-4">
        {storesLoading ? (
          <div className="animate-pulse w-full px-4 mb-4">
            {/* Table header shimmer */}
            <div className="h-12 bg-gray-200 rounded-lg w-full mb-2"></div>
            {/* Table rows shimmer */}
            <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-full"></div>
          </div>
        ) : storesError ? (
          <div className="p-8 text-center">
            <div className="text-red-600 mb-2">Error loading stores</div>
            <div className="text-sm text-gray-500 mb-4">{storesError}</div>
            <button
              onClick={() => dispatch(getAllStores({ page: 1, page_size: pageSize }))}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Try Again
            </button>
          </div>
        ) : (!displayData || displayData.length === 0) ? (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <div className="w-[48px] flex justify-center items-center h-[48px] rounded-full border border-[#E1E6E1] bg-[#F6F7F6] mb-4">
                <Store className="text-[#5F6D60] w-6 h-6" />
            </div>
            <p className="text-sm font-semibold text-[#5F6D60] text-center">
              {searchValue ? "No stores match your search criteria." : "No stores have been added yet."}
            </p>
          </div>
        ) : (
          <>
            {/* Table - Using Inventory Design Pattern */}
            <table className="w-full text-left rounded-lg overflow-hidden border border-[#E1E6E1] mx-4" style={{width: 'calc(100% - 2rem)'}}>
              <thead className="bg-[#F6F7F6] border-[#E1E6E1]">
                <tr className="text-sm">
                  <th className="px-4 py-4 font-semibold text-[#5F6D60] rounded-tl-lg rounded-bl-lg">
                    Store Name
                  </th>
                  <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                    Location
                  </th>
                  <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                    Manager
                  </th>
                  <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                    Stock Value
                  </th>
                  <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white border-[#E1E6E1]">
                {displayData && displayData.length > 0 ? displayData.map((store) => (
                  <tr 
                    key={store.id} 
                    className="text-sm cursor-pointer hover:bg-[#EDF7EE] border-b border-[#E1E6E1]"
                    onClick={() => handleViewStore(store.id)}
                  >
                    <td className="px-4 py-4 font-normal text-[#5F6D60] whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <Store className="h-4 w-4 text-primary" />
                          </div>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-semibold text-[#161D17]">
                            {store.name}
                          </div>
                          <div className="text-xs text-[#5F6D60]">
                            ID: {store.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-[#5F6D60] font-semibold">
                      <div>{store.location}</div>
                      <div className="text-xs text-gray-500">{store.state}</div>
                    </td>
                    <td className="px-4 py-4 text-[#5F6D60] font-semibold">
                      <div className="font-medium">{store.manager}</div>
                      <div className="text-xs text-gray-500">{store.manager_phone}</div>
                    </td>
                    <td className="px-4 py-4 text-[#5F6D60]">
                      <div className="font-medium">₦{store.stock_value?.toLocaleString() || 0}</div>
                      <div className="text-xs text-gray-500">{store.total_products} products</div>
                    </td>
                    <td className="px-4 py-4 text-[#344335] rounded-tr-lg rounded-br-lg">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        store.status === 'Active' 
                          ? 'bg-green-100 text-green-800'
                          : store.status === 'Inactive'
                          ? 'bg-red-100 text-red-800'
                          : store.status === 'Pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {store.status}
                      </span>
                    </td>
                  </tr>
                )) : null}
              </tbody>
            </table>

            {/* Pagination - using ProductInventoryPage approach */}
            <div className="border-t border-[#E1E6E1]">
              <PaginationComponentOne
                page={storesCurrentPage || currentPage}
                totalItems={storesTotalCount || 0}
                pageSize={pageSize}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StoresTable;
